/**
 * SnapshotRenderer — rendu autonome d'un snapshot figé : une page de garde
 * brandée (si le thème l'active) + les pages du rapport rendues avec leurs
 * données figées (aucun fetch). Destiné à l'aperçu snapshot et à la génération PDF.
 *
 * Dimensions FIGÉES : chaque page fait 1100×1556 px (ratio A4), rendue à 1:1 — AUCUNE
 * mise à l'échelle globale (les éléments sont dimensionnés pour cette taille). Le PDF est
 * produit par Playwright avec `page.pdf({ width:'1100px', height:'1556px' })` → 1 page
 * rapport = 1 page PDF, sans coupe ni marge fantôme. Sur les pages de contenu, l'en-tête
 * et le pied (si le thème les active) sont des bandeaux fixes ; la grille de widgets est
 * ajustée pour tenir entre les deux.
 */
import { useEffect } from 'react'
import GridLayout from 'react-grid-layout'
import WidgetRenderer from '../reports/WidgetRenderer'
import { ThemeCover, FONT_STACK, LogoSlot, str } from '../reportThemes/ThemeCover'
import { resolveChartPalette } from '../reports/chartPalette'
import { ChartPaletteProvider } from '../reports/ChartPaletteContext'
import type { ReportTheme, ThemeBlock } from '../../types/reportTheme'
import type { FrozenPage, SnapshotFull } from '../../types/snapshot'

const PAGE_WIDTH = 1100
const PAGE_HEIGHT = 1556
const HEADER_H = 104
const FOOTER_H = 76
const GRID_CONFIG = { cols: 120, rowHeight: 20, margin: [10, 10] as [number, number], containerPadding: [16, 16] as [number, number], maxRows: 69 }

const PRINT_CSS = `
@media print {
  @page { size: 1100px 1556px; margin: 0; }
  html, body { margin: 0 !important; background: #fff !important; }
  .snapshot-noprint { display: none !important; }
  .snapshot-canvas { padding: 0 !important; }
  .snapshot-doc { gap: 0 !important; }
  .snapshot-print-page {
    break-after: page; page-break-after: always;
    margin: 0 !important; box-shadow: none !important; border-radius: 0 !important;
  }
  .snapshot-print-page:last-child { break-after: auto; page-break-after: auto; }
}
`

interface SnapshotRendererProps {
  pagesFrozen: FrozenPage[]
  dataFrozen: SnapshotFull['dataFrozen']
}

function periodLabel(period: unknown): string {
  const p = (period ?? {}) as { start?: string; end?: string }
  if (!p.start || !p.end) return ''
  return `${p.start} – ${p.end}`
}

/** Page figée 1100×1556 (ratio A4), rendue à 1:1. */
function PrintPage({ children, testId }: { children: React.ReactNode; testId?: string }) {
  return (
    <div
      data-testid={testId}
      className="snapshot-print-page relative mx-auto overflow-hidden bg-white shadow-[0_8px_30px_rgba(16,24,64,0.12)]"
      style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT }}
    >
      {children}
    </div>
  )
}

export function SnapshotRenderer({ pagesFrozen, dataFrozen }: SnapshotRendererProps) {
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      // léger délai pour laisser ECharts finir de peindre avant capture PDF
      setTimeout(() => {
        ;(window as { __orion_snapshot_ready?: boolean }).__orion_snapshot_ready = true
      }, 400)
    })
    return () => cancelAnimationFrame(raf)
  }, [])

  const theme = dataFrozen.theme as ReportTheme | null
  const cover = theme?.cover as ThemeBlock | undefined
  const header = theme?.header as ThemeBlock | undefined
  const footer = theme?.footer as ThemeBlock | undefined
  const headerOn = !!theme && header?.enabled === true
  const footerOn = !!theme && footer?.enabled === true

  const clientLabel = `${dataFrozen.client.name} · ${dataFrozen.client.domain}`
  const period = periodLabel(dataFrozen.period)
  const mentions = str((footer as ThemeBlock | undefined)?.mentions) ?? 'Document confidentiel'

  // La grille est authorée sur 1556 px de haut. Quand des bandeaux réduisent la zone
  // disponible, on ajuste uniformément la grille pour qu'elle tienne, centrée horizontalement.
  const contentH = PAGE_HEIGHT - (headerOn ? HEADER_H : 0) - (footerOn ? FOOTER_H : 0)
  const gridScale = contentH / PAGE_HEIGHT
  const gridOffsetX = (PAGE_WIDTH - PAGE_WIDTH * gridScale) / 2
  const chartPalette = resolveChartPalette(theme?.chartPalette)

  return (
    <div
      className="snapshot-doc flex flex-col items-center gap-6"
      style={{ fontFamily: theme ? FONT_STACK[theme.fontFamily] : FONT_STACK.sans }}
    >
      <style>{PRINT_CSS}</style>

      <ChartPaletteProvider palette={chartPalette}>
      {theme && cover?.enabled ? (
        <PrintPage>
          <ThemeCover theme={theme} clientLabel={clientLabel} periodLabel={period} />
        </PrintPage>
      ) : null}

      {pagesFrozen.map((page, pageIndex) => {
        const widgets = ((page.layout as { widgets?: any[] } | null)?.widgets ?? []) as Array<{
          id: string
          type: string
          config: unknown
          x: number
          y: number
          w: number
          h: number
        }>
        const rglLayout = widgets.map((w) => ({ i: w.id, x: w.x, y: w.y, w: w.w, h: w.h }))

        return (
          <PrintPage key={page.id} testId="snapshot-page">
            <div className="flex h-full w-full flex-col">
              {/* En-tête brandé (bandeau fixe) */}
              {headerOn ? (
                <div
                  className="flex shrink-0 items-center justify-between px-10 text-white"
                  style={{ height: HEADER_H, backgroundColor: theme!.primaryColor }}
                >
                  <div className="flex items-center gap-4">
                    <LogoSlot url={theme!.clientLogoUrl} alt="Logo client" label="LOGO" size="md" />
                    <span className="text-2xl font-semibold">{dataFrozen.client.name}</span>
                  </div>
                  <span className="text-lg text-white/85">{period}</span>
                </div>
              ) : null}

              {/* Zone de contenu : grille ajustée pour tenir entre les bandeaux */}
              <div className="relative flex-1 overflow-hidden">
                <div
                  style={{
                    width: PAGE_WIDTH,
                    height: PAGE_HEIGHT,
                    transform: gridScale === 1 ? undefined : `translateX(${gridOffsetX}px) scale(${gridScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <GridLayout
                    layout={rglLayout}
                    width={PAGE_WIDTH}
                    autoSize={false}
                    style={{ height: PAGE_HEIGHT }}
                    gridConfig={GRID_CONFIG}
                    dragConfig={{ enabled: false }}
                    resizeConfig={{ enabled: false }}
                    dropConfig={{ enabled: false }}
                  >
                    {widgets.map((w) => (
                      <div key={w.id} className="relative h-full w-full">
                        <WidgetRenderer
                          widget={w as any}
                          data={(dataFrozen.widgets[w.id] ?? null) as any}
                          loading={false}
                          period={{ start: (dataFrozen.period as any)?.start, end: (dataFrozen.period as any)?.end } as any}
                        />
                      </div>
                    ))}
                  </GridLayout>
                </div>
              </div>

              {/* Pied de page brandé (bandeau fixe) */}
              {footerOn ? (
                <div
                  className="flex shrink-0 items-center justify-between border-t px-10 text-base text-[#8B8FA7]"
                  style={{ height: FOOTER_H, borderColor: '#EEF0F6' }}
                >
                  <span>{mentions}</span>
                  <span>
                    {dataFrozen.client.name} · Page {pageIndex + 1}
                  </span>
                </div>
              ) : null}
            </div>
          </PrintPage>
        )
      })}
      </ChartPaletteProvider>
    </div>
  )
}
